import { Component, Element, Prop, Host, h, Method, State, Watch } from '@stencil/core';
import Masonry from 'masonry-layout';

@Component({
  tag: 'spw-mosaic',
  styleUrl: 'spw-mosaic.scss',
  shadow: true,
})
export class SpwMosaic {
  @Element() el: HTMLSpwMosaicElement;

  /** Liste des éléments à afficher dans la mosaïque */
  @Prop() items?: any;
  /** Active le mode "masonry" pour une mise en page en grille ajustable */
  @Prop() isMasonry?: boolean = false;
  /** Nombre de colonnes à afficher en vue mobile */
  @Prop() colsMobile: number = 1;
  /** Nombre de colonnes à afficher en vue tablette */
  @Prop() colsTablet: number = 2;
  /** Nombre de colonnes à afficher en vue bureau */
  @Prop() colsDesktop: number = 3;

  private masonry: any;
  private observer: MutationObserver;

  @State() initialized = false;

  @Watch('items')
  itemsChanged(newValue: any[], oldValue: any[]) {
    if (newValue !== oldValue && this.isMasonry) {
      this.refreshMasonryLayout();
    }
  }

  @Watch('isMasonry')
  isMasonryChanged(newValue: boolean) {
    if (newValue) {
      this.initializeMasonry();
    } else {
      this.destroyMasonry();
    }
  }

  /** Attend le chargement de toutes les images pour garantir un rendu correct de la grille. */
  @Method()
  async waitForImages() {
    const images = this.el.shadowRoot.querySelectorAll('img');
    const loadPromises = Array.from(images).map(image => {
      return new Promise<void>(resolve => {
        if (image.complete) {
          resolve();
        } else {
          image.addEventListener('load', () => resolve(), { once: true });
        }
      });
    });

    await Promise.all(loadPromises);
  }

  private emitConfigurationChanged() {
    const event = new CustomEvent('mosaicConfigurationChanged', {
      detail: {
        colsMobile: this.colsMobile,
        colsTablet: this.colsTablet,
        colsDesktop: this.colsDesktop,
      },
      bubbles: true,
      composed: true,
    });
    this.el.dispatchEvent(event);
  }

  @Watch('colsMobile')
  @Watch('colsTablet')
  @Watch('colsDesktop')
  configurationChanged() {
    this.emitConfigurationChanged();
  }

  private async refreshMasonryLayout() {
    if (!this.isMasonry) return;

    await this.waitForImages();
    this.initializeMasonry();
  }

  componentDidLoad() {
    if (this.isMasonry) {
      this.initializeMasonry();
    }
    this.observeSlotChanges();
    this.emitConfigurationChanged();
  }

  componentDidUpdate() {
    if (this.isMasonry) {
      this.refreshMasonryLayout();
    }
  }

  disconnectedCallback() {
    this.destroyMasonry();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /** Initialise la mise en page Masonry pour organiser les éléments dans une grille fluide. */
  @Method()
  async initializeMasonry() {
    if (!this.isMasonry) return;

    requestAnimationFrame(() => {
      const slot = this.el.shadowRoot.querySelector('slot');
      const masonryItems = slot.assignedElements({ flatten: true });
      if (masonryItems.length > 0) {
        this.masonry = new Masonry(this.el, {
          itemSelector: 'spw-mosaic-item',
          columnWidth: 'spw-mosaic-item',
          percentPosition: true,
        });
        this.initialized = true;

        masonryItems.forEach(item => {
          const images = item.querySelectorAll('img');
          images.forEach(image => {
            if (!image.complete) {
              image.addEventListener('load', this.updateMasonryLayout.bind(this), { once: true });
            }
          });
        });
      }
    });
  }

  private updateMasonryLayout() {
    if (this.masonry && this.initialized && this.isMasonry) {
      this.masonry.layout();
    }
  }

  private destroyMasonry() {
    if (this.masonry) {
      this.masonry.destroy();
      this.masonry = null;
      this.initialized = false;
    }
  }

  private observeSlotChanges() {
    const slot = this.el.shadowRoot.querySelector('slot');
    this.observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && this.initialized && this.isMasonry) {
          this.initializeMasonry();
        }
      }
    });

    this.observer.observe(slot, { childList: true });
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-mosaic--no-masonry': !this.isMasonry,
    };
  }

  render() {
    return (
      <Host>
        <div class={this.elementClass}>
          <slot></slot>
        </div>
      </Host>
    );
  }
}
